// ============================================================================
// StudentFeeDetailsPage.jsx
// Premium UI redesign with modern card layout, smooth animations, and improved UX
// Route: /student-fee-details/:studentId
// ============================================================================

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../../utils/api";
import { alertDialog, confirmDialog } from "../../../utils/dialogs";
import { fetchFeeAssignments, updateFeeStatus, updateDueDate, consolidateSameMonthUnpaidFees, assignFeeToStudent } from "./feeApi";
import { bulkGenerateChallans, findChallanForFee } from "./challanApi";
import { buildChallanData, printChallanSheets } from "./challanDocument";
import { FEE_CATEGORIES, FEE_COMPONENTS } from "./feeConstants";

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const fmtCurrency = (n) => new Intl.NumberFormat("en-PK", { style: "currency", currency: "PKR", minimumFractionDigits: 0 }).format(n || 0);
const fmtClass    = (c) => c ? c.replace(/Grade/i, "Class").trim() : "—";
const fmtSection  = (s) => s ? s.replace(/Section\s*/i, "").trim() : "—";
const fmtMonth    = (m) => { if (!m) return "N/A"; const [y, mo] = m.split("-"); return `${MONTHS[Number(mo) - 1]} ${y}`; };
const fmtDueDate  = (day, m) => { if (!day || !m) return day || "N/A"; const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, Number(day)).toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric" }); };

// ── Design Tokens ─────────────────────────────────────────────────────────────
// Aligned to the ALS fee-module "ledger / challan" language: navy ink, gold
// (amber) rules, parchment surfaces, green seal, and IBM Plex Mono numerals —
// matching FeeManagement, the fee modals, and the Reports tab.
const C = {
  navy: "#0f1d36",
  navyLight: "#1c3461",
  navyGlow: "rgba(15,29,54,0.55)",
  blue: "#2a4a7f",
  blueLight: "#3d6098",
  blueSoft: "rgba(30,58,95,0.08)",
  blueGlow: "rgba(30,58,95,0.15)",
  green: "#2e7d32",
  greenLight: "#3a9b40",
  greenSoft: "rgba(46,125,50,0.08)",
  greenGlow: "rgba(46,125,50,0.15)",
  red: "#c41e24",
  redLight: "#e0494e",
  redSoft: "rgba(196,30,36,0.08)",
  amber: "#8a6c1f",
  amberLight: "#a8842a",
  amberSoft: "rgba(196,154,42,0.12)",
  purple: "#7c3aed",
  purpleLight: "#8b5cf6",
  purpleSoft: "rgba(124,58,237,0.08)",
  slate: "#5a5e6a",
  slateLight: "#8e92a0",
  slateLighter: "#e2e8f0",
  border: "#e8e9ed",
  bg: "#f8f9fc",
  white: "#ffffff",
  text: "#1a1d23",
  textSecondary: "#5a5e6a",
  // ledger accents
  gold: "#8a6c1f",
  parchment: "#fbf8f2",
  ledgerLine: "#ddd3bd",
};

// Monospace tabular numerals for every money/ID figure — the ledger signature.
const mono = { fontFamily: "'IBM Plex Mono', monospace", fontVariantNumeric: "tabular-nums" };

// ── Status dot (replaces emoji status icons — see no-emoji-icons rule) ─────────
const StatusDot = ({ color }) => (
  <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
);

// ── Tooltip Wrapper Component ─────────────────────────────────────────────────
const TooltipWrapper = ({ children, label, color = C.navy }) => (
  <div style={{
    position: "relative",
    display: "inline-flex",
  }}
  className="btn-wrapper">
    {children}
    <span style={{
      position: "absolute",
      bottom: "calc(100% + 8px)",
      left: "50%",
      transform: "translateX(-50%)",
      background: color,
      color: "#fff",
      fontSize: "10px",
      fontWeight: 600,
      padding: "4px 12px",
      borderRadius: "6px",
      whiteSpace: "nowrap",
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      letterSpacing: "0.3px",
      zIndex: 10,
    }}
    className="btn-tooltip"
    >
      {label}
      <span style={{
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        border: "6px solid transparent",
        borderTopColor: color,
      }} />
    </span>
    <style>{`
      .btn-wrapper:hover .btn-tooltip {
        opacity: 1;
      }
    `}</style>
  </div>
);

// ── Reusable Components ──────────────────────────────────────────────────────

// Gradient Card
const Card = ({ children, className, glow = false, gradient = false, style = {} }) => (
  <div style={{
    background: gradient ? `linear-gradient(135deg, ${C.navy}, ${C.navyLight})` : C.white,
    borderRadius: "16px",
    border: gradient ? "none" : `1px solid ${C.border}`,
    boxShadow: glow 
      ? `0 8px 32px ${C.blueGlow}` 
      : "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.02)",
    overflow: "hidden",
    marginBottom: "20px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    ...style
  }}>
    {children}
  </div>
);

// Stat Card
const StatCard = ({ label, value, icon, color, bg, subtitle }) => (
  <div style={{
    background: C.white,
    border: `1px solid ${C.ledgerLine}`,
    borderTop: `3px solid ${color}`,
    borderRadius: "12px",
    padding: "16px 20px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "default",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(15,29,54,0.05)",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow = `0 8px 24px ${color}22`;
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "0 1px 3px rgba(15,29,54,0.05)";
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: "10.5px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px" }}>{label}</div>
        <div style={{ ...mono, fontSize: "23px", fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
        {subtitle && <div style={{ fontSize: "12px", color: C.slateLight, marginTop: "4px" }}>{subtitle}</div>}
      </div>
      <div style={{
        width: 46,
        height: 46,
        borderRadius: "12px",
        background: `${color}14`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: color,
        flexShrink: 0,
      }}>
        {icon}
      </div>
    </div>
  </div>
);

// Button
const Btn = ({ children, onClick, variant = "primary", size = "md", disabled = false, icon, style = {} }) => {
  const variants = {
    primary: { bg: C.navy, color: C.white, hoverBg: C.navyLight },
    blue: { bg: C.blue, color: C.white, hoverBg: C.blueLight },
    green: { bg: C.green, color: C.white, hoverBg: C.greenLight },
    red: { bg: C.red, color: C.white, hoverBg: C.redLight },
    ghost: { bg: "transparent", color: C.textSecondary, hoverBg: C.bg, border: `1px solid ${C.border}` },
    outline: { bg: "transparent", color: C.blue, hoverBg: C.blueSoft, border: `1px solid ${C.blue}` },
  };
  
  const variantStyles = variants[variant] || variants.primary;
  const sizes = {
    sm: { padding: "6px 12px", fontSize: "12px", gap: "4px" },
    md: { padding: "8px 16px", fontSize: "13px", gap: "6px" },
    lg: { padding: "10px 20px", fontSize: "14px", gap: "8px" },
  };
  
  const sizeStyles = sizes[size] || sizes.md;

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: sizeStyles.gap,
        padding: sizeStyles.padding,
        fontSize: sizeStyles.fontSize,
        fontWeight: 600,
        borderRadius: "10px",
        border: variantStyles.border || "none",
        background: disabled ? C.slateLighter : variantStyles.bg,
        color: disabled ? C.slate : variantStyles.color,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        boxShadow: variant === "primary" && !disabled ? "0 4px 12px rgba(10,22,40,0.2)" : "none",
        ...style
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = variantStyles.hoverBg;
          if (variant === "primary") e.currentTarget.style.boxShadow = "0 6px 20px rgba(10,22,40,0.3)";
          if (variant === "blue") e.currentTarget.style.boxShadow = "0 6px 20px rgba(37,99,235,0.3)";
          if (variant === "green") e.currentTarget.style.boxShadow = "0 6px 20px rgba(5,150,105,0.3)";
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = variantStyles.bg;
          if (variant === "primary" || variant === "blue" || variant === "green") {
            e.currentTarget.style.boxShadow = "none";
          }
        }
      }}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
};

// Status Badge
const StatusBadge = ({ status }) => {
  const map = {
    Paid:    { bg: C.greenSoft, color: C.green, label: "PAID" },
    Waived:  { bg: C.purpleSoft, color: C.purple, label: "WAIVED" },
    Partial: { bg: C.amberSoft, color: C.amber, label: "PARTIAL" },
    Unpaid:  { bg: C.redSoft, color: C.red, label: "UNPAID" },
  };
  const { bg, color, label } = map[status] || map.Unpaid;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 700,
      background: bg,
      color: color,
      letterSpacing: "0.4px",
    }}>
      <StatusDot color={color} /> {label}
    </span>
  );
};

// Shared field styling for the challan builder form
const challanSectionLabel = {
  fontSize: "11px",
  fontWeight: 800,
  color: C.navy,
  textTransform: "uppercase",
  letterSpacing: "0.8px",
  paddingBottom: "8px",
  marginBottom: "12px",
  borderBottom: `1px solid ${C.border}`,
};

const challanFieldLabel = {
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  color: C.slate,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: "6px",
};

const challanInput = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: "10px",
  border: `1.5px solid ${C.border}`,
  fontSize: "13px",
  background: C.white,
  color: C.text,
  boxSizing: "border-box",
};

// Modal
const Modal = ({ isOpen, onClose, title, subtitle, children }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      onClick={(e) => e.target === e.currentTarget && onClose()} 
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,22,40,0.6)",
        backdropFilter: "blur(12px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
        animation: "fadeIn 0.25s ease",
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.96) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{
        background: C.white,
        borderRadius: "20px",
        width: "min(95vw, 560px)",
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}>
        <div style={{
          padding: "20px 24px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}>
          <div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: C.text }}>{title}</div>
            {subtitle && <div style={{ fontSize: "13px", color: C.slate, marginTop: "2px" }}>{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            style={{
              background: C.bg,
              border: "none",
              borderRadius: "10px",
              width: 36,
              height: 36,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              color: C.slate,
              transition: "all 0.2s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = C.redSoft;
              e.currentTarget.style.color = C.red;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = C.bg;
              e.currentTarget.style.color = C.slate;
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const StudentFeeDetailsPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [student, setStudent] = useState(null);
  const [feeAssignments, setFeeAssignments] = useState({});
  const [previousDuesHistory, setPreviousDuesHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentRemarks, setPaymentRemarks] = useState("");
  const [selectedFeeForPayment, setSelectedFeeForPayment] = useState(null);
  const [selectedFeeForView, setSelectedFeeForView] = useState(null);
  // inline due-date editing: { feeId, rawMonth, categoryId, value (day string) }
  const [editingDue, setEditingDue] = useState(null);
  // Challan builder — used both for the post-payment "next month" prompt and the
  // "New Challan" button. `mode` decides the copy and the defaults.
  const [challanForm, setChallanForm] = useState(null);
  // { sourceFee, month, dueDate, selectedCategories, components }
  const [pendingOpenNextMonth, setPendingOpenNextMonth] = useState(
    () => !!(location.state?.openNextMonth)
  );
  const [pendingPaidFeeKey, setPendingPaidFeeKey] = useState(
    () => location.state?.paidFeeKey || null
  );

  useEffect(() => { loadStudentData(); }, [studentId]);

  const loadStudentData = async () => {
    try {
      const token = localStorage.getItem("als_token");
      if (!token) {
        alertDialog("Please login again", { title: "Login Required" });
        navigate("/login");
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        localStorage.removeItem("als_token");
        localStorage.removeItem("als_role");
        alertDialog("Session expired. Please login again.", { title: "Session Expired" });
        navigate("/login");
        return;
      }

      if (!res.ok) throw new Error("Failed to fetch students");
      const allStudents = await res.json();
      const found = (Array.isArray(allStudents) ? allStudents : []).find(s => s.id === parseInt(studentId));
      if (!found) { alertDialog("Student not found", { title: "Not Found" }); navigate(-1); return; }
      setStudent({
        ...found,
        class: found.class || found.class_name || "",
        section: found.section || found.class_section || "",
        rollNo: found.rollNo || found.student_id || found.id,
      });

      const all = await consolidateSameMonthUnpaidFees(studentId);
      const studentFees = all[studentId] || all[parseInt(studentId)] || {};
      setFeeAssignments(studentFees);

      const today = new Date(); today.setHours(0, 0, 0, 0);
      let duesHistory = JSON.parse(localStorage.getItem(`previous_dues_${studentId}`) || "[]");
      const tracked = new Set(duesHistory.map(d => d.originalFeeId));
      let updated = false;
      Object.entries(studentFees).forEach(([entryKey, cat]) => {
        if (cat.status === "Paid" || cat.status === "Waived") return;
        const [yr, mo] = (cat.month || "").split("-");
        const dueObj = new Date(Number(yr), Number(mo) - 1, Number(cat.dueDate || "10"));
        dueObj.setHours(0, 0, 0, 0);
        if (today > dueObj && !tracked.has(entryKey)) {
          duesHistory.push({
            id: Date.now() + Math.random(),
            amount: cat.netPayable || 0,
            reason: `Outstanding: ${cat.categoryLabel} - ${fmtMonth(cat.month)}`,
            date: dueObj.toISOString().split("T")[0],
            paid: false,
            addedAt: new Date().toISOString(),
            originalFeeId: entryKey,
            autoAdded: true
          });
          tracked.add(entryKey);
          updated = true;
        }
      });
      if (updated) localStorage.setItem(`previous_dues_${studentId}`, JSON.stringify(duesHistory));
      setPreviousDuesHistory(duesHistory);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const nextMonthValue = (rawMonth) => {
    if (!rawMonth || !rawMonth.includes("-")) {
      const d = new Date();
      d.setMonth(d.getMonth() + 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    const [y, m] = rawMonth.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const openNextMonthModal = (fee) => {
    const cats = Array.isArray(fee.selectedCategories) && fee.selectedCategories.length
      ? [...fee.selectedCategories]
      : [fee.categoryId].filter(Boolean);
    setChallanForm({
      mode: "next",
      sourceFee: fee,
      month: nextMonthValue(fee.rawMonth),
      dueDate: fee.rawDueDay || "10",
      selectedCategories: cats,
      components: (fee.components || []).map((c) => ({ ...c })),
    });
  };

  // "New Challan" starts empty on the upcoming month so the operator picks the
  // categories and due date themselves.
  const openNewChallanModal = () => {
    setChallanForm({
      mode: "new",
      sourceFee: null,
      month: nextMonthValue(null),
      dueDate: "10",
      selectedCategories: [],
      components: [],
    });
  };

  // Open Next Month flow when navigated here from Fee Management
  useEffect(() => {
    if (loading || !pendingOpenNextMonth || !Object.keys(feeAssignments).length) return;

    const entries = Object.entries(feeAssignments);
    let paidEntry = null;
    if (pendingPaidFeeKey && feeAssignments[pendingPaidFeeKey]) {
      paidEntry = [pendingPaidFeeKey, feeAssignments[pendingPaidFeeKey]];
    } else {
      paidEntry = entries.find(([, cat]) => cat.status === "Paid" || cat.status === "Waived")
        || entries[entries.length - 1];
    }
    if (!paidEntry) {
      setPendingOpenNextMonth(false);
      return;
    }

    const [key, cat] = paidEntry;
    openNextMonthModal({
      id: key,
      categoryId: cat.categoryId || key.replace(`_${cat.month}`, ""),
      categoryLabel: cat.categoryLabel,
      selectedCategories: cat.selectedCategories || [cat.categoryId].filter(Boolean),
      rawMonth: cat.month,
      rawDueDay: cat.dueDate || "10",
      components: cat.components || [],
      academicYear: cat.academicYear,
    });
    setPendingOpenNextMonth(false);
    setPendingPaidFeeKey(null);
    // Clear router state so refresh doesn't reopen forever
    navigate(location.pathname, { replace: true, state: {} });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, feeAssignments, pendingOpenNextMonth, pendingPaidFeeKey]);

  const handleMarkAsPaid = async (feeId, feeData) => {
    const amount = feeData.netPayable || 0;
    if (amount <= 0) { alertDialog("Invalid amount", { title: "Invalid Amount" }); return; }
    const record = {
      id: Date.now(),
      amount,
      method: paymentMethod,
      remarks: paymentRemarks || `Payment for ${feeData.categoryLabel} - ${feeData.month}`,
      date: new Date().toISOString(),
      studentId,
      studentName: student.name,
      feeId,
      feeCategory: feeData.categoryLabel
    };
    const payments = JSON.parse(localStorage.getItem("payment_history") || "[]");
    localStorage.setItem("payment_history", JSON.stringify([...payments, record]));
    const dues = JSON.parse(localStorage.getItem(`previous_dues_${studentId}`) || "[]");
    if (dues.some(d => !d.paid)) {
      const cleared = dues.map(d => ({ ...d, paid: true, clearedAt: new Date().toISOString() }));
      localStorage.setItem(`previous_dues_${studentId}`, JSON.stringify(cleared));
      setPreviousDuesHistory(cleared);
    }

    const categoryId = feeData.categoryId || feeId?.replace(`_${feeData.rawMonth}`, "");
    const rawMonth = feeData.rawMonth || feeData.month;
    await updateFeeStatus(studentId, categoryId, rawMonth, "Paid", {
      feeId,
      paymentMethod,
      paymentDate: new Date().toISOString().split("T")[0],
      remarks: paymentRemarks || null,
    });

    alertDialog(`Payment of ${fmtCurrency(amount)} processed for ${feeData.categoryLabel}!`, { title: "Payment Processed", variant: "success" });
    setPaymentAmount("");
    setPaymentMethod("cash");
    setPaymentRemarks("");
    setSelectedFeeForPayment(null);
    await loadStudentData();

    // Offer next-month challan with previous categories prefilled
    openNextMonthModal({
      ...feeData,
      id: feeId,
      categoryId,
      rawMonth,
      rawDueDay: feeData.rawDueDay || "10",
      selectedCategories: feeData.selectedCategories,
      components: feeData.components,
    });
  };

  const handleAddToPreviousDues = (feeData) => {
    const entryKey = feeData.id;
    if (previousDuesHistory.some(d => d.originalFeeId === entryKey && !d.paid)) {
      alertDialog("Already tracked in Previous Dues.", { title: "Already Tracked" });
      return;
    }
    const newDue = {
      id: Date.now(),
      amount: feeData.netPayable || 0,
      reason: `Outstanding: ${feeData.categoryLabel} - ${feeData.month}`,
      date: new Date().toISOString().split("T")[0],
      paid: false,
      addedAt: new Date().toISOString(),
      originalFeeId: entryKey
    };
    const updated = [...previousDuesHistory, newDue];
    setPreviousDuesHistory(updated);
    localStorage.setItem(`previous_dues_${studentId}`, JSON.stringify(updated));
    alertDialog("Fee moved to Previous Dues.", { title: "Previous Dues Updated", variant: "success" });
  };

  const handleDeleteFee = async (fee) => {
    if (!(await confirmDialog(`Delete "${fee.categoryLabel} - ${fee.month}"? This cannot be undone.`, { title: "Delete Fee", confirmLabel: "Delete Fee", variant: "danger" }))) return;
    const all = JSON.parse(localStorage.getItem("fee_assignments_v2") || "{}");
    const key = all[studentId] ? studentId : String(parseInt(studentId));
    if (all[key]?.[fee.id]) {
      delete all[key][fee.id];
      localStorage.setItem("fee_assignments_v2", JSON.stringify(all));
    }
    const dues = JSON.parse(localStorage.getItem(`previous_dues_${studentId}`) || "[]");
    const remaining = dues.filter(d => d.originalFeeId !== fee.id);
    if (remaining.length !== dues.length) {
      localStorage.setItem(`previous_dues_${studentId}`, JSON.stringify(remaining));
      setPreviousDuesHistory(remaining);
    }
    loadStudentData();
  };

  const printChallan = async ({ student, fee }) => {
    const unpaidDues = previousDuesHistory.filter((due) => !due.paid).reduce((sum, due) => sum + (due.amount || 0), 0);
    let storedChallan = await findChallanForFee(
      student.id,
      fee.id || `${fee.categoryId}_${fee.rawMonth || fee.month}`,
    );
    if (!storedChallan) {
      const generated = await bulkGenerateChallans([{
        student,
        feeStructure: {
          ...fee,
          month: fee.rawMonth || fee.month,
          dueDate: fee.rawDueDay || fee.dueDate,
        },
      }]);
      storedChallan = generated.generated[0] || null;
    }
    if (!storedChallan) {
      await alertDialog("The challan could not be generated.", { title: "Challan Error" });
      return;
    }

    const data = buildChallanData({
      student,
      feeStructure: {
        ...fee,
        month: fee.rawMonth || fee.month,
        dueDate: fee.rawDueDay || fee.dueDate,
      },
      challan: storedChallan,
      previousDues: unpaidDues,
    });

    printChallanSheets([data], `Challan — ${data.challanNumber}`);
  };

  const printDuesChallan = ({ student, previousDuesHistory }) => {
    const unpaid = previousDuesHistory.filter((due) => !due.paid);
    const challanNum = `DUE-${student.id}-${Date.now().toString().slice(-4)}`;
    const today = new Date();

    const data = buildChallanData({
      student,
      feeStructure: {
        categoryLabel: "Previous Dues",
        month: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`,
        dueDate: today.getDate(),
        components: unpaid.map((due) => ({
          componentLabel: due.reason || "Previous Due",
          amount: due.amount || 0,
        })),
        concessionAmount: 0,
      },
      challan: { challanNumber: challanNum },
    });

    printChallanSheets([data], `Dues Challan — ${challanNum}`);
  };

  const getFeeRows = () => {
    const unpaidKeys = new Set(previousDuesHistory.filter(d => !d.paid).map(d => d.originalFeeId));
    return Object.entries(feeAssignments).map(([key, cat]) => ({
      id: key,
      categoryId: cat.categoryId || key.replace(`_${cat.month}`, ""),
      categoryLabel: cat.categoryLabel,
      selectedCategories: cat.selectedCategories || [cat.categoryId].filter(Boolean),
      month: fmtMonth(cat.month),
      rawMonth: cat.month,
      dueDate: fmtDueDate(cat.dueDate, cat.month),
      rawDueDay: cat.dueDate || "10",
      issueDate: new Date().toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric" }),
      netPayable: cat.netPayable,
      status: cat.status || "Unpaid",
      paymentMethod: cat.paymentMethod || null,
      paymentDate: cat.paymentDate || null,
      remarks: cat.remarks || null,
      components: cat.components,
      subtotal: cat.subtotal,
      concessionAmount: cat.concessionAmount,
      movedToPreviousDues: unpaidKeys.has(key),
    }));
  };

  // ── Icons ───────────────────────────────────────────────────────────────────
  const Icons = {
    back: () => (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
      </svg>
    ),
    user: () => (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    print: () => (
      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
        <path d="M6 9V3h12v6"/>
        <rect x="6" y="15" width="12" height="6" rx="2"/>
      </svg>
    ),
    edit: () => (
      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M17 3l4 4-7 7H10v-4l7-7z"/>
        <path d="M4 20h16"/>
      </svg>
    ),
    trash: () => (
      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M4 7h16M10 11v6M14 11v6M5 7l1 13a2 2 0 002 2h8a2 2 0 002-2l1-13M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/>
      </svg>
    ),
    eye: () => (
      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
      </svg>
    ),
    pay: () => (
      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"/>
        <path d="M8 12h8M12 8v8"/>
      </svg>
    ),
    docList: ({ size = 22 } = {}) => (
      <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h6M9 17h6"/>
      </svg>
    ),
    alert: ({ size = 22 } = {}) => (
      <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/>
      </svg>
    ),
    wallet: ({ size = 22 } = {}) => (
      <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2"/><path d="M21 12h-5a2 2 0 0 0 0 4h5v-4z"/>
      </svg>
    ),
    search: ({ size = 40 } = {}) => (
      <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
      </svg>
    ),
    clock: ({ size = 13 } = {}) => (
      <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
      </svg>
    ),
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      background: C.bg,
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: 48,
          height: 48,
          border: `3px solid ${C.border}`,
          borderTopColor: C.navy,
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
          margin: "0 auto 16px",
        }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ color: C.slate, fontSize: "14px", fontWeight: 500 }}>Loading student details...</div>
      </div>
    </div>
  );

  if (!student) return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      background: C.bg,
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.parchment, border: `1px solid ${C.ledgerLine}`, color: C.slate, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Icons.search />
        </div>
        <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, color: C.text, marginBottom: "8px" }}>Student Not Found</h2>
        <p style={{ color: C.slate, marginBottom: "20px" }}>The student you're looking for doesn't exist.</p>
        <Btn onClick={() => navigate(-1)}>Go Back</Btn>
      </div>
    </div>
  );

  const feeRows = getFeeRows();
  const totalPreviousDues = previousDuesHistory.filter(d => !d.paid).reduce((s, d) => s + d.amount, 0);
  const currentTotal = Object.values(feeAssignments).reduce((s, c) => s + (c.netPayable || 0), 0);
  const grandTotal = currentTotal + totalPreviousDues;
  const initials = student.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px" }}>

      <button
        onClick={() => {
          // Go exactly one step back to the page that opened this view
          // (campus fee hub / challan / management). Avoid hard jump to /fees.
          if (window.history.length > 1) navigate(-1);
          else navigate("/fees");
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: "10px",
          padding: "9px 16px",
          fontSize: "13px",
          fontWeight: 600,
          color: C.slate,
          cursor: "pointer",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
      >
        <Icons.back />
        Back
      </button>

      {/* Header */}
      <Card gradient style={{ marginBottom: "24px", borderBottom: `4px solid ${C.gold}` }}>
        <div style={{
          padding: "24px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "16px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "20px",
              flexShrink: 0,
            }}>
              {initials}
            </div>

            <div>
              <h1 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "24px",
                fontWeight: 700,
                color: "#fff",
                margin: 0,
                letterSpacing: "-0.3px",
              }}>
                {student.name}
              </h1>
              <div style={{ display: "flex", gap: "20px", marginTop: "6px", flexWrap: "wrap" }}>
                {[
                  ["ID", student.id],
                  ["Roll No", student.rollNo],
                  ["Class", fmtClass(student.class) || "—"],
                  ["Section", fmtSection(student.section) || "—"]
                ].map(([lbl, val]) => (
                  <span key={lbl} style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)" }}>
                    <span style={{ fontWeight: 700, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.5px" }}>{lbl} </span>
                    <strong style={{ ...mono, color: "#fff" }}>{val}</strong>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: C.amberLight, textTransform: "uppercase", letterSpacing: "0.8px" }}>
              Grand Total Payable
            </div>
            <div style={{ ...mono, fontSize: "32px", fontWeight: 700, color: "#fff", marginTop: "4px", letterSpacing: "-0.5px" }}>
              {fmtCurrency(grandTotal)}
            </div>
            {totalPreviousDues > 0 && (
              <div style={{ fontSize: "12px", color: "#fca5a5", marginTop: "2px" }}>
                Includes {fmtCurrency(totalPreviousDues)} in previous dues
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ── Stats Grid ────────────────────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "16px",
        marginBottom: "24px",
      }}>
        <StatCard
          label="Current Fees"
          value={fmtCurrency(currentTotal)}
          color={C.blue}
          icon={<Icons.docList />}
        />
        <StatCard
          label="Previous Dues"
          value={fmtCurrency(totalPreviousDues)}
          color={C.red}
          icon={<Icons.alert />}
        />
        <StatCard
          label="Total Payable"
          value={fmtCurrency(grandTotal)}
          color={C.green}
          icon={<Icons.wallet />}
        />
      </div>

      {/* ── Fee Table ──────────────────────────────────────────────────────── */}
      <Card>
        <div style={{
          padding: "18px 24px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "16px", color: C.text }}>
              Fee Details
            </div>
            <div style={{ fontSize: "13px", color: C.slate, marginTop: "2px" }}>
              {feeRows.length} fee entr{feeRows.length === 1 ? "y" : "ies"} found
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              background: C.blueSoft,
              padding: "4px 14px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              color: C.blue,
            }}>
              {feeRows.filter(f => f.status === "Unpaid").length} unpaid
            </div>
            <Btn
              variant="primary"
              size="sm"
              onClick={openNewChallanModal}
              icon={<Icons.docList size={15} />}
            >
              New Challan
            </Btn>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          {feeRows.length === 0 ? (
            <div style={{
              padding: "60px 20px",
              textAlign: "center",
              color: C.slate,
            }}>
              <div style={{ width: 64, height: 64, borderRadius: "14px", background: C.parchment, border: `1px solid ${C.ledgerLine}`, color: C.slateLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <Icons.docList size={30} />
              </div>
              <div style={{ fontWeight: 700, fontSize: "16px", color: C.text }}>No fees assigned</div>
              <div style={{ fontSize: "13px", marginTop: "6px" }}>Use <strong>New Challan</strong> above to create the first challan for this student.</div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Fee Category", "Month", "Due Date", "Net Payable", "Status", "Actions"].map(h => (
                    <th key={h} style={{
                      textAlign: h === "Net Payable" ? "right" : "left",
                      padding: "11px 16px",
                      fontSize: "10.5px",
                      fontWeight: 700,
                      color: C.amber,
                      background: C.amberSoft,
                      borderBottom: `2px solid ${C.amber}`,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      whiteSpace: "nowrap",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {feeRows.map((fee, idx) => (
                  <tr
                    key={fee.id}
                    style={{
                      background: idx % 2 === 0 ? C.white : C.bg,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = C.blueSoft;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = idx % 2 === 0 ? C.white : C.bg;
                    }}
                  >
                    <td style={{
                      padding: "14px 16px",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: C.text,
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      {fee.categoryLabel}
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      fontSize: "13px",
                      color: C.textSecondary,
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      {fee.month}
                    </td>
                    {/* Editable Due Date cell */}
                    <td style={{
                      padding: "14px 16px",
                      fontSize: "13px",
                      color: C.textSecondary,
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      {editingDue?.feeId === fee.id ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <input
                            type="number" min="1" max="31"
                            value={editingDue.value}
                            onChange={e => setEditingDue(p => ({ ...p, value: e.target.value }))}
                            autoFocus
                            style={{ width: "56px", padding: "5px 8px", borderRadius: "8px", border: `1.5px solid ${C.blue}`, fontSize: "13px", outline: "none" }}
                          />
                          <button
                            onClick={async () => {
                              const day = editingDue.value;
                              if (!day || isNaN(day) || day < 1 || day > 31) {
                                alertDialog("Enter a valid day (1–31)", { title: "Invalid" }); return;
                              }
                              await updateDueDate(studentId, editingDue.categoryId, editingDue.rawMonth, day);
                              setEditingDue(null);
                              loadStudentData();
                            }}
                            style={{ background: "#2e7d32", color: "#fff", border: "none", borderRadius: "6px", padding: "5px 10px", cursor: "pointer", fontWeight: 700, fontSize: "12px" }}>✓</button>
                          <button
                            onClick={() => setEditingDue(null)}
                            style={{ background: "#f1f5f9", color: "#5a5e6a", border: "none", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", fontSize: "12px" }}>✕</button>
                        </div>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          {fee.dueDate}
                          {fee.status !== "Paid" && fee.status !== "Waived" && (
                            <button
                              onClick={() => setEditingDue({ feeId: fee.id, rawMonth: fee.rawMonth, categoryId: fee.categoryId, value: fee.rawDueDay || "10" })}
                              title="Edit due date"
                              style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: "12px", fontWeight: 700, padding: "0 2px" }}>✎</button>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={{
                      ...mono,
                      padding: "14px 16px",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: C.text,
                      textAlign: "right",
                      borderBottom: `1px solid ${C.ledgerLine}`,
                    }}>
                      {fmtCurrency(fee.netPayable)}
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      {fee.movedToPreviousDues && fee.status === "Unpaid" ? (
                        <span style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: 700,
                          background: C.amberSoft,
                          color: C.amber,
                          letterSpacing: "0.4px",
                        }}>
                          <Icons.clock /> IN DUES
                        </span>
                      ) : (
                        <StatusBadge status={fee.status} />
                      )}
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {/* View Breakdown Button */}
                        <TooltipWrapper label="View Breakdown">
                          <Btn
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedFeeForView(fee)}
                            style={{ padding: "6px 10px" }}
                          >
                            <Icons.eye />
                          </Btn>
                        </TooltipWrapper>

                        {fee.status !== "Paid" && fee.status !== "Waived" ? (
                          <>
                            {/* Pay — the only way to record a payment for this fee */}
                            <TooltipWrapper label={fee.movedToPreviousDues ? "In Dues" : "Pay Now"} color={fee.movedToPreviousDues ? C.slate : C.green}>
                              <Btn
                                size="sm"
                                variant={fee.movedToPreviousDues ? "ghost" : "green"}
                                onClick={() => setSelectedFeeForPayment(fee.id)}
                                disabled={fee.movedToPreviousDues}
                                style={{ padding: "6px 10px" }}
                              >
                                <Icons.pay />
                              </Btn>
                            </TooltipWrapper>

                            {/* Delete Fee Button */}
                            <TooltipWrapper label="Delete Fee" color={C.red}>
                              <Btn
                                size="sm"
                                variant="red"
                                onClick={() => handleDeleteFee(fee)}
                                style={{ padding: "6px 10px" }}
                              >
                                <Icons.trash />
                              </Btn>
                            </TooltipWrapper>
                          </>
                        ) : (
                          <TooltipWrapper label="Generate Next Month Challan" color={C.green}>
                            <Btn
                              size="sm"
                              variant="green"
                              onClick={() => openNextMonthModal(fee)}
                              style={{ padding: "6px 10px", fontSize: "11px", fontWeight: 700 }}
                            >
                              Next Month
                            </Btn>
                          </TooltipWrapper>
                        )}

                        {/* Print Challan Button */}
                        <TooltipWrapper label="Print Challan">
                          <Btn
                            size="sm"
                            variant="primary"
                            onClick={() => printChallan({ student, fee })}
                            style={{ padding: "6px 10px" }}
                          >
                            <Icons.print />
                          </Btn>
                        </TooltipWrapper>
                      </div>
                    </td>
                  </tr>
                ))}

                {/* Previous Dues Row */}
                {previousDuesHistory.filter(d => !d.paid).length > 0 && (
                  <tr style={{
                    background: C.amberSoft,
                    borderTop: `2px solid ${C.amber}`,
                  }}>
                    <td colSpan="3" style={{
                      padding: "14px 16px",
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: C.amber,
                          display: "inline-block",
                        }} />
                        <span style={{ fontWeight: 700, color: C.amber, fontSize: "13px" }}>
                          Previous Dues (Outstanding)
                        </span>
                        <span style={{
                          fontSize: "11px",
                          background: C.amber,
                          color: C.white,
                          padding: "1px 10px",
                          borderRadius: "20px",
                          fontWeight: 600,
                        }}>
                          {previousDuesHistory.filter(d => !d.paid).length} item{previousDuesHistory.filter(d => !d.paid).length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </td>
                    <td style={{
                      ...mono,
                      padding: "14px 16px",
                      fontWeight: 700,
                      color: C.red,
                      textAlign: "right",
                      borderBottom: `1px solid ${C.ledgerLine}`,
                      fontSize: "14px",
                    }}>
                      {fmtCurrency(totalPreviousDues)}
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      <StatusBadge status="Unpaid" />
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {/* Print Dues Challan Button */}
                        <TooltipWrapper label="Print Dues Challan">
                          <Btn
                            size="sm"
                            variant="primary"
                            onClick={() => printDuesChallan({ student, previousDuesHistory })}
                            style={{ padding: "6px 10px" }}
                          >
                            <Icons.print />
                          </Btn>
                        </TooltipWrapper>

                        {/* Pay Dues Button */}
                        <TooltipWrapper label="Pay All Dues" color={C.green}>
                          <Btn
                            size="sm"
                            variant="green"
                            onClick={() => setSelectedFeeForPayment("previous_dues")}
                            style={{ padding: "6px 10px" }}
                          >
                            <Icons.pay />
                          </Btn>
                        </TooltipWrapper>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* ── Modals ────────────────────────────────────────────────────────── */}

      {/* View Breakdown Modal */}
      {selectedFeeForView && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedFeeForView(null)}
          title="Fee Breakdown"
          subtitle={`${selectedFeeForView.categoryLabel} · ${selectedFeeForView.month}`}
        >
          <div style={{ marginBottom: "16px" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "10px",
            }}>
              {[
                ["Due Date", selectedFeeForView.dueDate],
                ["Status", selectedFeeForView.status || "Unpaid"],
                ["Amount", fmtCurrency(selectedFeeForView.netPayable)],
              ].map(([k, v]) => (
                <div key={k} style={{
                  background: C.bg,
                  borderRadius: "10px",
                  padding: "10px 14px",
                }}>
                  <div style={{ fontSize: "10px", color: C.slate, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                    {k}
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: 700, color: C.text, marginTop: "2px" }}>
                    {v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            border: `1px solid ${C.border}`,
            borderRadius: "12px",
            overflow: "hidden",
            marginBottom: "16px",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{
                    padding: "10px 14px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: C.slate,
                    background: C.bg,
                    textAlign: "left",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                  }}>Component</th>
                  <th style={{
                    padding: "10px 14px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: C.slate,
                    background: C.bg,
                    textAlign: "right",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                  }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(selectedFeeForView.components || []).map((c, i) => (
                  <tr key={i}>
                    <td style={{
                      padding: "10px 14px",
                      fontSize: "13px",
                      borderTop: `1px solid ${C.border}`,
                      color: C.text,
                    }}>{c.componentLabel || c.componentId}</td>
                    <td style={{
                      padding: "10px 14px",
                      fontSize: "13px",
                      textAlign: "right",
                      fontWeight: 600,
                      borderTop: `1px solid ${C.border}`,
                      color: C.text,
                    }}>{fmtCurrency(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {(selectedFeeForView.concessionAmount || 0) > 0 && (
                  <tr>
                    <td style={{
                      padding: "10px 14px",
                      fontSize: "13px",
                      color: C.green,
                      borderTop: `1px solid ${C.border}`,
                    }}>Discount</td>
                    <td style={{
                      padding: "10px 14px",
                      fontSize: "13px",
                      color: C.green,
                      textAlign: "right",
                      fontWeight: 600,
                      borderTop: `1px solid ${C.border}`,
                    }}>−{fmtCurrency(selectedFeeForView.concessionAmount)}</td>
                  </tr>
                )}
                <tr style={{ background: C.blueSoft }}>
                  <td style={{
                    padding: "12px 14px",
                    fontWeight: 800,
                    color: C.blue,
                    fontSize: "14px",
                  }}>Net Payable</td>
                  <td style={{
                    padding: "12px 14px",
                    textAlign: "right",
                    fontWeight: 800,
                    color: C.blue,
                    fontSize: "16px",
                  }}>{fmtCurrency(selectedFeeForView.netPayable)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <TooltipWrapper label="Print Challan">
              <Btn
                variant="primary"
                onClick={() => {
                  printChallan({ student, fee: selectedFeeForView });
                  setSelectedFeeForView(null);
                }}
                icon={<Icons.print />}
              >
                Print
              </Btn>
            </TooltipWrapper>
            <Btn
              variant="ghost"
              onClick={() => setSelectedFeeForView(null)}
              style={{ marginLeft: "auto" }}
            >
              Close
            </Btn>
          </div>
        </Modal>
      )}

      {/* Payment Modal */}
      {selectedFeeForPayment && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedFeeForPayment(null)}
          title="Process Payment"
        >
          {selectedFeeForPayment === "previous_dues" ? (
            <div style={{
              background: C.amberSoft,
              border: `1px solid ${C.amber}`,
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
            }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: C.amber, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Paying Outstanding Dues
              </div>
              <div style={{ fontSize: "28px", fontWeight: 800, color: C.amber, marginTop: "4px" }}>
                {fmtCurrency(totalPreviousDues)}
              </div>
            </div>
          ) : (
            <div style={{
              background: C.blueSoft,
              border: `1px solid ${C.blue}33`,
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
            }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: C.blue, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                {feeRows.find(f => f.id === selectedFeeForPayment)?.categoryLabel} · {feeRows.find(f => f.id === selectedFeeForPayment)?.month}
              </div>
              <div style={{ fontSize: "28px", fontWeight: 800, color: C.blue, marginTop: "4px" }}>
                {fmtCurrency(feeRows.find(f => f.id === selectedFeeForPayment)?.netPayable || 0)}
              </div>
            </div>
          )}

          <div style={{ marginBottom: "14px" }}>
            <label style={{
              display: "block",
              fontSize: "11px",
              fontWeight: 700,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}>Payment Amount</label>
            <input
              type="number"
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: `1.5px solid ${C.border}`,
                fontSize: "13px",
                color: C.text,
                background: C.white,
                outline: "none",
              }}
              placeholder="Leave empty to pay full amount"
              value={paymentAmount}
              onChange={e => setPaymentAmount(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label style={{
              display: "block",
              fontSize: "11px",
              fontWeight: 700,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}>Payment Method</label>
            <select
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: `1.5px solid ${C.border}`,
                fontSize: "13px",
                color: C.text,
                background: C.white,
                outline: "none",
              }}
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
            >
              {[
                ["cash", "Cash"],
                ["bank_transfer", "Bank Transfer"],
                ["cheque", "Cheque"],
                ["online", "Online Payment"]
              ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              fontSize: "11px",
              fontWeight: 700,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}>Remarks</label>
            <textarea
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: `1.5px solid ${C.border}`,
                fontSize: "13px",
                color: C.text,
                background: C.white,
                outline: "none",
                resize: "none",
                minHeight: "60px",
              }}
              rows={2}
              placeholder="Add any notes…"
              value={paymentRemarks}
              onChange={e => setPaymentRemarks(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <Btn
              variant="green"
              onClick={() => {
                if (selectedFeeForPayment === "previous_dues") {
                  const amt = parseFloat(paymentAmount);
                  if (amt > totalPreviousDues) {
                    alertDialog("Amount cannot exceed total previous dues", { title: "Invalid Amount" });
                    return;
                  }
                  const payAmt = amt || totalPreviousDues;
                  let remaining = payAmt;
                  const updated = [...previousDuesHistory];
                  const records = [];
                  const all = JSON.parse(localStorage.getItem("fee_assignments_v2") || "{}");
                  const aKey = all[studentId] !== undefined ? studentId : parseInt(studentId);
                  const sFees = { ...(all[aKey] || {}) };
                  let changed = false;
                  for (let i = 0; i < updated.length && remaining > 0; i++) {
                    if (!updated[i].paid && updated[i].amount <= remaining) {
                      updated[i].paid = true;
                      updated[i].paidAt = new Date().toISOString();
                      remaining -= updated[i].amount;
                      const linked = updated[i].originalFeeId ? sFees[updated[i].originalFeeId] : null;
                      if (linked && linked.status !== "Paid" && linked.status !== "Waived") {
                        sFees[updated[i].originalFeeId] = {
                          ...linked,
                          status: "Paid",
                          paymentMethod,
                          paymentDate: new Date().toISOString().split("T")[0],
                          remarks: paymentRemarks || "Cleared via Previous Dues"
                        };
                        changed = true;
                        records.push({
                          id: Date.now() + Math.random(),
                          amount: updated[i].amount,
                          method: paymentMethod,
                          remarks: paymentRemarks || `Cleared via Previous Dues — ${linked.categoryLabel}`,
                          date: new Date().toISOString(),
                          studentId,
                          studentName: student.name,
                          feeId: updated[i].originalFeeId,
                          feeCategory: linked.categoryLabel,
                          type: "previous_dues_clearance"
                        });
                      } else {
                        records.push({
                          id: Date.now() + Math.random(),
                          amount: updated[i].amount,
                          method: paymentMethod,
                          remarks: paymentRemarks || updated[i].reason || "Payment for previous dues",
                          date: new Date().toISOString(),
                          studentId,
                          studentName: student.name,
                          type: "previous_dues"
                        });
                      }
                    }
                  }
                  const existing = JSON.parse(localStorage.getItem("payment_history") || "[]");
                  localStorage.setItem("payment_history", JSON.stringify([...existing, ...records]));
                  if (changed) {
                    all[aKey] = sFees;
                    localStorage.setItem("fee_assignments_v2", JSON.stringify(all));
                  }
                  setPreviousDuesHistory(updated);
                  localStorage.setItem(`previous_dues_${studentId}`, JSON.stringify(updated));
                  alertDialog("Payment processed successfully!", { title: "Payment Processed", variant: "success" });
                  setPaymentAmount("");
                  setPaymentMethod("cash");
                  setPaymentRemarks("");
                  setSelectedFeeForPayment(null);
                  loadStudentData();
                } else {
                  const sel = feeRows.find(f => f.id === selectedFeeForPayment);
                  if (sel) {
                    const amtToPay = paymentAmount ? parseFloat(paymentAmount) : sel.netPayable;
                    if (amtToPay > sel.netPayable) {
                      alertDialog("Amount cannot exceed net payable", { title: "Invalid Amount" });
                      return;
                    }
                    handleMarkAsPaid(selectedFeeForPayment, {
                      categoryLabel: sel.categoryLabel,
                      categoryId: sel.categoryId,
                      month: sel.month,
                      rawMonth: sel.rawMonth,
                      rawDueDay: sel.rawDueDay,
                      netPayable: amtToPay,
                      selectedCategories: sel.selectedCategories,
                      components: sel.components,
                    });
                  }
                }
              }}
              style={{ flex: 1, justifyContent: "center" }}
              icon={<svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>}
            >
              Confirm Payment
            </Btn>
            <Btn
              variant="ghost"
              onClick={() => setSelectedFeeForPayment(null)}
              style={{ flex: 1, justifyContent: "center" }}
            >
              Cancel
            </Btn>
          </div>
        </Modal>
      )}

      {/* Challan builder — used for both a brand new challan and next month's */}
      {challanForm && (
        <Modal
          isOpen={!!challanForm}
          onClose={() => setChallanForm(null)}
          title={challanForm.mode === "new" ? "Generate New Challan" : "Generate Next Month Challan"}
          subtitle={`${student.name} · ${fmtClass(student.class) || "—"}${fmtSection(student.section) ? ` · Section ${fmtSection(student.section)}` : ""} · Roll ${student.rollNo}`}
        >
          <div style={{ fontSize: "13px", color: C.slate, marginBottom: "20px", lineHeight: 1.55 }}>
            {challanForm.mode === "new"
              ? "Choose the billing month and due date, tick the fee categories to bill, then fine-tune the amounts."
              : "Categories from the paid challan are prefilled. Change the month, due date or categories before generating."}
          </div>

          <div style={{ marginBottom: "22px" }}>
            <div style={challanSectionLabel}>Billing Period</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={challanFieldLabel}>Month</label>
                <input
                  type="month"
                  value={challanForm.month}
                  onChange={(e) => setChallanForm((p) => ({ ...p, month: e.target.value }))}
                  style={challanInput}
                />
              </div>
              <div>
                <label style={challanFieldLabel}>Due Date (day of month)</label>
                <input type="number" min="1" max="31" value={challanForm.dueDate}
                  onChange={(e) => setChallanForm((p) => ({ ...p, dueDate: e.target.value }))}
                  style={challanInput} />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "22px" }}>
            <div style={challanSectionLabel}>Fee Categories</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "8px" }}>
              {FEE_CATEGORIES.filter((c) => c.id !== "custom").map((cat) => {
                const checked = challanForm.selectedCategories.includes(cat.id);
                return (
                  <label key={cat.id} style={{
                    display: "flex", alignItems: "center", gap: "8px", padding: "10px 12px",
                    borderRadius: "10px", border: `1.5px solid ${checked ? C.blue : C.border}`,
                    background: checked ? C.blueSoft : C.white, cursor: "pointer", fontSize: "12.5px",
                    fontWeight: 600, color: checked ? C.blue : C.text, transition: "all 0.15s",
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setChallanForm((prev) => {
                          const selectedCategories = checked
                            ? prev.selectedCategories.filter((id) => id !== cat.id)
                            : [...prev.selectedCategories, cat.id];
                          const componentMap = {
                            monthly: ["tuition"],
                            exam: ["examination"],
                            sports: ["sports"],
                            library: ["library"],
                            transport: ["transport"],
                            hostel: ["hostel"],
                          };
                          const wanted = new Set(selectedCategories.flatMap((id) => componentMap[id] || []));
                          // Keep existing amounts where possible; add defaults for newly checked
                          const kept = prev.components.filter((c) => wanted.has(c.componentId));
                          const keptIds = new Set(kept.map((c) => c.componentId));
                          selectedCategories.forEach((id) => {
                            (componentMap[id] || []).forEach((compId) => {
                              if (keptIds.has(compId)) return;
                              const fc = FEE_COMPONENTS.find((x) => x.id === compId);
                              if (fc) {
                                kept.push({ componentId: fc.id, componentLabel: fc.label, amount: fc.defaultValue });
                                keptIds.add(compId);
                              }
                            });
                          });
                          return { ...prev, selectedCategories, components: kept };
                        });
                      }}
                      style={{ accentColor: C.blue }}
                    />
                    {cat.label}
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <div style={challanSectionLabel}>Amounts</div>
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
              {(challanForm.components || []).length === 0 ? (
                <div style={{ padding: "22px 16px", fontSize: "13px", color: C.slate, textAlign: "center" }}>
                  Select at least one fee category above.
                </div>
              ) : challanForm.components.map((comp, idx) => (
                <div key={`${comp.componentId}-${idx}`} style={{
                  display: "flex", alignItems: "center", gap: "10px", padding: "11px 14px",
                  borderBottom: idx === challanForm.components.length - 1 ? "none" : `1px solid ${C.border}`,
                  background: idx % 2 === 0 ? C.white : C.bg,
                }}>
                  <span style={{ flex: 1, fontSize: "13px", fontWeight: 600, color: C.text }}>{comp.componentLabel}</span>
                  <input
                    type="number"
                    min="0"
                    value={comp.amount}
                    onChange={(e) => setChallanForm((p) => ({
                      ...p,
                      components: p.components.map((c, i) => i === idx ? { ...c, amount: parseFloat(e.target.value) || 0 } : c),
                    }))}
                    style={{ ...mono, width: "120px", padding: "7px 10px", borderRadius: "8px", border: `1.5px solid ${C.border}`, textAlign: "right", fontSize: "13px", fontWeight: 600 }}
                  />
                  <button
                    onClick={() => setChallanForm((p) => ({ ...p, components: p.components.filter((_, i) => i !== idx) }))}
                    title={`Remove ${comp.componentLabel}`}
                    style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontWeight: 700, fontSize: "16px", lineHeight: 1, padding: "0 4px" }}
                  >×</button>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: C.blueSoft, border: `1px solid ${C.blue}22`, borderRadius: "12px",
            padding: "14px 18px", marginBottom: "20px",
          }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: C.blue, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Challan Total
            </span>
            <span style={{ ...mono, fontSize: "20px", fontWeight: 800, color: C.blue }}>
              {fmtCurrency((challanForm.components || []).reduce((s, c) => s + (Number(c.amount) || 0), 0))}
            </span>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <Btn
              variant="primary"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={async () => {
                if (!challanForm.selectedCategories.length || !challanForm.components.length) {
                  alertDialog("Select at least one fee category / component", { title: "Missing" });
                  return;
                }
                if (!challanForm.month) {
                  alertDialog("Select a month", { title: "Missing Month" });
                  return;
                }
                const dd = parseInt(challanForm.dueDate, 10);
                if (!dd || dd < 1 || dd > 31) {
                  alertDialog("Enter a valid due date (1–31)", { title: "Invalid Due Date" });
                  return;
                }
                const labels = challanForm.selectedCategories
                  .map((id) => FEE_CATEGORIES.find((c) => c.id === id)?.label || id);
                const categoryId = challanForm.selectedCategories.length > 1 ? "custom" : challanForm.selectedCategories[0];
                const categoryLabel = labels.join(" + ");
                const payload = {
                  studentId: student.id,
                  studentName: student.name,
                  studentClass: student.class,
                  studentRollNo: student.rollNo,
                  studentSection: student.section,
                  categoryId,
                  categoryLabel,
                  categoryPrefix: challanForm.selectedCategories.length > 1 ? "MIX" : "FEE",
                  selectedCategories: [...challanForm.selectedCategories],
                  components: challanForm.components,
                  academicYear: challanForm.sourceFee?.academicYear || "2025-2026",
                  month: challanForm.month,
                  dueDate: String(dd),
                  concession: null,
                };
                const created = await assignFeeToStudent(payload);
                const genResult = await bulkGenerateChallans([{
                  student,
                  feeStructure: created,
                }]);
                setChallanForm(null);
                await loadStudentData();
                if (genResult.generated.length > 0) {
                  alertDialog(`Challan ${genResult.generated[0].challanNumber} generated. You can print it from this page or from Challan Printing.`, { title: "Challan Generated", variant: "success" });
                } else if (genResult.skipped.length > 0) {
                  alertDialog(genResult.skipped[0].reason, { title: "Challan Skipped" });
                }
              }}
            >
              Generate Challan
            </Btn>
            <Btn variant="ghost" style={{ flex: 1, justifyContent: "center" }} onClick={() => setChallanForm(null)}>
              Cancel
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StudentFeeDetailsPage;
